import React, { useState, useEffect } from "react";
import {
  Backdrop,
  Button,
  FormFieldSet,
  LogoLoader,
  TableAdv,
} from "../../../../components";
import { useDispatch, useSelector } from "react-redux";
import { recruiterReportsStart } from "../../../../redux/slices/reports/reportsSlice";
import { getEndOfDay, getStartOfDay } from "../../../../utils/common";
import { usePagination } from "../../../../hooks";
import { formatWords2 } from "../../../../utils/stringUtils";
import { aiCallSummaryReportApi, creditUtilizationReportApi, resumeMiningHistoryReportApi } from "../../../../redux/slices/reports/reportsApi";
import { Link } from "react-router-dom";
import { dateFormator } from "../../../../utils";

const recruiterSummaryColumns = [
    {
      key: "recruiterName",
      label: "Recruiter Name",
    },
    {
      key: "email",
      label: "Email Id",
    },
    {
      key: "jobsAllocated",
      label: "Jobs Allocated",
    },
    {
      key: "internalSubmissions",
      label: "Internal Submissions",
    },
    {
      key: "clientSubmissions",
      label: "Client Submissions",
    },
    {
      key: "interviews",
      label: "Interviews",
    },
    {
      key: "offerSent",
      label: "Offer Sent",
    },
    {
      key: "starts",
      label: "Starts",
    },
    {
      key: "backouts",
      label: "Backouts",
    },
];

const creditUtilizationColumns = [
  {
    key: "jobboard",
    label: "Job Board",
    render: (row) => (
      <span>{`${formatWords2(row?.jobboard)} ${row.type === 1 ? `(Monster)` : row.type === 2 ? `(career Builder)` : `(AI Call)`}`}</span>
    )
  },
  {
    key: "recruitername",
    label: "User Name",
  },
  {
    key: "email",
    label: "Email ID",
  },
  {
    key: "creditsassigned",
    label: "Credits Assigned",
  },
  {
    key: "creditsused",
    label: "Credits Used",
  },
];

const aiCallSummaryColumns = [
  {
    key: "jobCode",
    label: "Job Code",
    render: (row) => (
      <Link
        to={`/ats/jobs/job-details`}
        className="text-info-400 flex items-center gap-.5"
        onClick={() => {
          sessionStorage.setItem("selectedJobUiid", row?.jobId);
          sessionStorage.setItem("prevJobsPage", window.location.pathname + window.location.search);
        }}
      >
        <span className="mx-2">{row.jobCode}</span>
      </Link>
    ),
  },
  {
    key: "addedBy",
    label: "Initiated By"
  },
  {
    key: "totalCalls",
    label: "Total Calls"
  },
  {
    key: "initiated",
    label: "Initiated"
  },
  {
    key: "skipped",
    label: "Skipped"
  },
  {
    key: "success",
    label: "Success"
  },
  {
    key: "failed",
    label: "Failed"
  }
];

const SOURCE_LABEL = { 1: "Monster", 2: "CareerBuilder" };

const resumeMiningColumns = [
  {
    key: "jobCode",
    label: "Job Code",
    render: (row) => (
      <Link
        to={`/ats/jobs/job-details`}
        className="text-info-400 flex items-center gap-.5"
        onClick={() => {
          sessionStorage.setItem("selectedJobUiid", row?.jobPostId);
          sessionStorage.setItem("prevJobsPage", window.location.pathname + window.location.search);
        }}
      >
        <span className="mx-2">{row.jobCode}</span>
      </Link>
    ),
  },
  {
    key: "scheduleDate",
    label: "Scheduled Date(UTC)",
    // render: (row) => (
    //   <span>{row?.scheduleDate ? new Date(row.scheduleDate).toLocaleDateString() : "—"}</span>
    // ),
  },
  {
  key: "executedAt",
  label: "Executed At(UTC)",
  },
  // {
  //   key: "interval",
  //   label: "Scheduled Time",
  //   render: (row) => <span>{row?.interval || "—"}</span>,
  // },
  {
    key: "maximumProfiles",
    label: "Scheduled Resumes",
  },
  {
    key: "totalFound",
    label: "Total Found",
  },
  {
    key: "existingCount",
    label: "Existing Candidates",
  },
  {
    key: "pulledCount",
    label: "Pulled Candidates",
  },
  {
    key: "failedCount",
    label: "Failed Candidates Count",
  },
  {
    key: "source",
    label: "Source",
    render: (row) => <span>{SOURCE_LABEL[row?.source] || "—"}</span>,
  },
  {
    key: "scheduledBy",
    label: "Scheduled By",
  },
];

const RecruiterReports = ({ selectedReport, setSelectedReport }) => {
  const dispatch = useDispatch();
  const { recruiterReport, loading } = useSelector((state) => state.reports) || {};
  const { page, limit, setPage, handleLimitChange } = usePagination(
    10,
    (newPage, newLimit) => {},
  );
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    const before10Days = new Date();
    before10Days.setDate(today.getDate() - 10);
    return { from: new Date(), to: new Date() };
  });
  const [tableColumns, setTableColumns] = useState(
    selectedReport?.tempRepId === "recruiter_summary_sheet" ? recruiterSummaryColumns :
      selectedReport?.tempRepId === "recruiter_jobboard_credit_utilization" ? creditUtilizationColumns :
        selectedReport?.tempRepId === "recruiter_kult_ai_call_summary" ? aiCallSummaryColumns :
          selectedReport?.tempRepId === "recruiter_resume_mining" ? resumeMiningColumns : []
  );
  const [tableId, setTableId] = useState(selectedReport?.tempRepId);
  const [tableRows, setTableRows] = useState([]);
  const [totalCount, setTotalCount] = useState();
  const [stateLoading, setStateLoading] = useState(false);

  const handleDateRangeChange = (range) => {
    if (!range || !range.from || !range.to) return;
    const fromISO = range.from ? range.from : null;
    const toISO = range.to ? range.to : null;
    setDateRange({ from: fromISO, to: toISO });
  };

  const fetchRecruiterReports = () => {
    const payload = {
      coll: "Submission",
      fil: {
        addedOn: {
          type: "RECRUITER_REPORT",
          value: [getStartOfDay(dateRange?.from), getEndOfDay(dateRange?.to)],
        },
      },
      useAggregate: true,
      queryMode: "RECRUITER_REPORT",
      lim: limit,
    };
    dispatch(recruiterReportsStart(payload));
  };

  const fetchCreditutilizationReport = async () => {
    try {
      setStateLoading(true);
      const response = await creditUtilizationReportApi({
        coll: "JobBoardCreditHistory",
        fil: {uiid: {
          type: "CREDIT_UTILIZATION_REPORT", 
          value: [getStartOfDay(dateRange?.from), getEndOfDay(dateRange?.to)]
        }},
        useAggregate: true,
        pg: page,
        lim: limit,
      });
      if (response?.success) {
        const rows = response?.data?.data?.[0]?.data?.map((report, i) => ({
          id: i + 100,
          jobboard: report?.integrationId?.name,
          recruitername: formatWords2(report?.userId?.firstName) + " " + formatWords2(report?.userId?.lastName),
          email: report?.userId?.email?.primary,
          creditsassigned: report?.creditsAssigned,
          creditsused: report?.creditsUsed,
          category: report?.integrationId?.category,
          type: report?.integrationId?.type,
        }));
        setTableRows(rows);
        setTotalCount(response?.data?.data?.[0]?.totalCount);
      }
    } catch (error) {
      console.error("Error Fetrching Credit utilization Report: ", error)
    } finally {
      setStateLoading(false);
    }
  };

  const fetchAiCallSummaryReport = async () => {
    try {
      setStateLoading(true);
      const response = await aiCallSummaryReportApi({
        coll: "AiCall",
        fil: {uiid: {
          type: "AI_CALL_SUMMARY_REPORT", 
          value: [getStartOfDay(dateRange?.from), getEndOfDay(dateRange?.to)]
        }},
        useAggregate: true,
        extret: "uiid firstName lastName",
        pg: page,
        lim: limit,
      });
      if (response?.success) {
        const rows = response?.data?.data?.[0]?.data?.map((report, i) => ({
          id: i + 100,
          jobCode: report?.jobPostId?.jobCode,
          addedBy: report?.addedBy?.firstName + " " + report?.addedBy?.lastName,
          totalCalls: report?.totalCalls || 0,
          initiated: report?.initiated || 0,
          skipped: report?.skipped || 0,
          success: report?.success || 0,
          failed: report?.failed || 0,
          jobId: report?.jobPostId?.uiid
        }));
        setTableRows(rows);
        setTotalCount(response?.data?.data?.[0]?.totalCount);
      }
    } catch (error) {
      console.error("Error fetching AiCall Report: ", error);
    } finally {
      setStateLoading(false);
    }
  };

  const fetchResumeMiningHistoryReport = async () => {
    try {
      setStateLoading(true);
      const response = await resumeMiningHistoryReportApi({
        coll: "ResumeMiningHistory",
        fil: {
          uiid: {
            type: "RESUME_MINING_HISTORY_REPORT",
            value: [getStartOfDay(dateRange?.from), getEndOfDay(dateRange?.to)],
          },
        },
        useAggregate: true,
        pg: page,
        lim: limit,
      });
      if (response?.success) {
        const rows = response?.data?.data?.[0]?.data?.map((report, i) => ({
          id: i + 100,
          jobCode: report?.jobCode || "—",
          scheduleDate: (() => {
            if (!report?.scheduleDate) return "—";

            const date = new Date(report.scheduleDate);

            if (report?.interval) {
              const [hours, minutes] = report.interval.split(":");

              date.setUTCHours(Number(hours));
              date.setUTCMinutes(Number(minutes));
              date.setUTCSeconds(0);
            }

            return dateFormator(date, "day-shortMonth-year-time-utc");
          })(),


          executedAt: report?.addedOn
            ? dateFormator(report.addedOn, "day-shortMonth-year-time-utc")
            : "—",
          // interval: report?.interval,
          maximumProfiles: report?.maximumProfiles ?? 0,
          totalFound: report?.totalFound ?? 0,
          existingCount: report?.existingCount ?? 0,
          pulledCount: report?.pulledCount ?? 0,
          failedCount: report?.failedCount ?? 0,
          source: report?.source,
          scheduledBy: report?.scheduledBy?.trim() || "—",
          jobPostId: report?.jobPostId,
        }));
        setTableRows(rows || []);
        setTotalCount(response?.data?.data?.[0]?.totalCount);
      }
    } catch (error) {
      console.error("Error fetching Resume Mining History Report: ", error);
    } finally {
      setStateLoading(false);
    }
  };

  useEffect(() => {
    if (selectedReport?.tempRepId === "recruiter_summary_sheet") {
      fetchRecruiterReports();
    } else if (selectedReport?.tempRepId === "recruiter_jobboard_credit_utilization") {
      fetchCreditutilizationReport();
    } else if (selectedReport?.tempRepId === "recruiter_kult_ai_call_summary") {
      fetchAiCallSummaryReport();
    } else if (selectedReport?.tempRepId === "recruiter_resume_mining") {
      fetchResumeMiningHistoryReport();
    }
  }, [dateRange, limit, page]);

  useEffect(() => {
    if (selectedReport?.tempRepId === "recruiter_summary_sheet") {
      const rows = recruiterReport?.data?.data?.map((report, i) => ({
        id: i + 100,
        recruiterName: formatWords2(report?.user?.firstName) + " " + formatWords2(report?.user?.lastName),
        email: report?.user?.email,
        jobsAllocated: report?.jobsAllocated || 0,
        internalSubmissions: report?.internalSubmissions || 0,
        clientSubmissions: report?.clientSubmissions || 0,
        interviews: report?.interviews || 0,
        offerSent: report?.offerSent || 0,
        starts: report?.starts || 0,
        backouts: report?.backouts || 0,
      }));
      setTableRows(rows);
      setTotalCount(recruiterReport?.data?.total);
    }    
  }, [recruiterReport]);

  useEffect(() => {
    if (selectedReport?.tempRepId === "recruiter_summary_sheet") {
      setTableColumns(recruiterSummaryColumns);
    } else if (selectedReport?.tempRepId === "recruiter_jobboard_credit_utilization") {
      setTableColumns(creditUtilizationColumns);
    } else if (selectedReport?.tempRepId === "recruiter_kult_ai_call_summary") {
      setTableColumns(aiCallSummaryColumns);
    } else if (selectedReport?.tempRepId === "recruiter_resume_mining") {
      setTableColumns(resumeMiningColumns);
    } else {
      setTableColumns([]);
    }

    if (selectedReport?.tempRepId) {
      setTableId(selectedReport?.tempRepId);
    }
  }, [selectedReport]);

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <Button
        title={`${selectedReport?.reportName}`}
        color="black"
        variant="text"
        size="md"
        fromLeft
        icon="arrow_left_alt"
        className="self-start text-lg font-semibold"
        onClick={() => setSelectedReport(null)}
      />

      <div className="flex-1 overflow-auto">
        <TableAdv
          columns={tableColumns}
          rows={tableRows}
          showCheckbox={false}
          rowClickeble={false}
          onPaginationChange={setPage}
          onLimitChange={handleLimitChange}
          serverPagination={true}
          page={page}
          limit={limit}
          // onSelectionChange={(ids) => setSelectedIds(ids)}
          showSlNo={true}
          totalCount={totalCount || 0}
          className="text-blue-600"
          tableId={tableId}
          toolbarRight={{
            search: false,
            filter: false,
            download: false,
            add: [
              {
                component: (
                  <div className="flex items-center gap-2">
                    <FormFieldSet
                      noTitle
                      placeholder="Select date range"
                      type="datepickerRange"
                      size="sm"
                      value={dateRange}
                      onChange={(range) => {
                        handleDateRangeChange(range);
                      }}
                      shadow={true}
                    />
                  </div>
                ),
              },
            ],
          }}
        />
      </div>

      <Backdrop isOpen={selectedReport?.tempRepId === "recruiter_summary_sheet" ? loading : stateLoading}>
        <LogoLoader />
      </Backdrop>
    </div>
  );
};

export default RecruiterReports;
